'use client'

import { useState, useEffect, useRef, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { SongArtwork } from '@/components/ui/song-artwork'
import {
  Music,
  DollarSign,
  Users,
  Radio,
  ExternalLink,
  Heart,
  Share2,
  History,
  Coffee,
  ChevronRight,
  ArrowRight,
  Calendar,
  MapPin,
} from 'lucide-react'
import {
  SiInstagram,
  SiSpotify,
  SiYoutube,
  SiFacebook,
  SiTiktok,
  SiSnapchat,
} from 'react-icons/si'
import Image from 'next/image'
import { toast } from 'sonner'
import { m, AnimatePresence } from 'framer-motion'
import { useTranslations } from 'next-intl'
import type { ReactionType, FeedbackType } from '@/types'
import { useDemo } from '../demo-context'
import { useDemoActions } from '../use-demo-actions'
import { useDemoSession } from '../use-demo-session-tracking'
import { useCoachMarks } from '../coach-marks/use-coach-marks'
import { DEMO_FEATURED_SONGS } from '../demo-data'
import type { DemoQueueRequest } from '../demo-types'

import { ContainerContext } from './inline-sheet'
import { InlineSheet } from './inline-sheet'
import { SocialIcon } from './social-icon'
import { NowPlayingSection } from './now-playing-section'
import { QueueSection } from './queue-section'
import { RequestSheet } from './request-sheet'
import { TipSheet, BoostSheet } from './tip-sheet'
import { ReactionBar } from './reaction-bar'
import { FeedbackSection } from './feedback-section'
import { TimerBanner } from './timer-banner'
import { ArtistProfile } from './artist-profile'
import { StartingSoonOverlay } from './starting-soon-overlay'

export function DemoAudienceView() {
  const { state, dispatch, sortedQueue, activeTimers } = useDemo()
  const actions = useDemoActions(dispatch)

  return (
    <AnimatePresence mode="wait">
      {state.phase === 'pre-event' ? (
        <m.div
          key="pre-event"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, x: -30 }}
          transition={{ duration: 0.4 }}
          className="h-full flex flex-col bg-zinc-950"
        >
          <StartingSoonOverlay eventTitle={state.event.title} />
          <ArtistProfile
            artist={state.artist}
            event={state.event}
            featuredSongs={DEMO_FEATURED_SONGS}
          />
        </m.div>
      ) : (
        <m.div
          key="live"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="h-full"
        >
          <LiveAudienceView state={state} actions={actions} sortedQueue={sortedQueue} activeTimers={activeTimers} />
        </m.div>
      )}
    </AnimatePresence>
  )
}

// Separate component to avoid conditional hooks
function LiveAudienceView({
  state,
  actions,
  sortedQueue,
  activeTimers,
}: {
  state: ReturnType<typeof useDemo>['state']
  actions: ReturnType<typeof useDemoActions>
  sortedQueue: ReturnType<typeof useDemo>['sortedQueue']
  activeTimers: ReturnType<typeof useDemo>['activeTimers']
}) {
  const demoSession = useDemoSession()
  const { activeCoachMark } = useCoachMarks()
  const t = useTranslations('demo')
  const { artist, songs, nowPlaying, lastPlayed, playedSongs, visitorCount, breakStatus, settings, visitorUpvotedRequestIds, visitorHasTipped, visitorReactionTokens } = state

  // Filter to only show public timers
  const publicTimers = useMemo(() => activeTimers.filter(t => t.isPublic), [activeTimers])

  // Squelched song IDs set for the request sheet
  const squelchedSongIds = useMemo(
    () => new Set(state.squelchedSongs.filter(s => s.until.getTime() > state.currentTime).map(s => s.songId)),
    [state.squelchedSongs, state.currentTime]
  )

  // Container ref for portaling sheets
  const containerRef = useRef<HTMLDivElement>(null)
  const [container, setContainer] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    setContainer(containerRef.current)
  }, [])

  // Sheet state
  const [isRequestSheetOpen, setIsRequestSheetOpen] = useState(false)
  const [isTipSheetOpen, setIsTipSheetOpen] = useState(false)
  const [isBoostSheetOpen, setIsBoostSheetOpen] = useState(false)
  const [boostingRequest, setBoostingRequest] = useState<DemoQueueRequest | null>(null)
  const [isFeedbackSheetOpen, setIsFeedbackSheetOpen] = useState(false)
  const [isSongHistoryOpen, setIsSongHistoryOpen] = useState(false)

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleRequestSong = (song: { id: string; title: string }, tipCents: number) => {
    actions.requestSong(song.id, tipCents, true)
    setIsRequestSheetOpen(false)
    const coachPrompted = activeCoachMark?.id === 'request-song'
    demoSession?.trackMilestone('request_sent', { songId: song.id, tipCents })
    demoSession?.trackMilestone('feature_used', { feature: 'request', coachPrompted })
    toast.success(
      tipCents > 0
        ? t('requestedWithBoost', { songTitle: song.title, amount: (tipCents / 100).toFixed(0) })
        : t('requestedToast', { songTitle: song.title })
    )
  }

  const handleUpvote = (request: DemoQueueRequest) => {
    if (visitorUpvotedRequestIds.has(request.id)) {
      toast.error(t('alreadyUpvoted'))
      return
    }
    actions.upvoteSong(request.id, true)
  }

  const handleBoost = (request: DemoQueueRequest, tipCents: number) => {
    actions.addTipToRequest(request.id, tipCents, true)
    setBoostingRequest(null)
    setIsBoostSheetOpen(false)
    toast.success(t('boostAdded', { amount: (tipCents / 100).toFixed(0), songTitle: request.song.title }))
  }

  const handleDirectTip = (tipCents: number) => {
    actions.sendDirectTip(tipCents, true)
    setIsTipSheetOpen(false)
    const coachPrompted = activeCoachMark?.id === 'send-tip'
    demoSession?.trackMilestone('tip_sent', { tipCents })
    demoSession?.trackMilestone('feature_used', { feature: 'tip', coachPrompted })
    toast.success(t('tipSent', { amount: (tipCents / 100).toFixed(0), artistName: artist.name }))
  }

  const handleReaction = (type: ReactionType) => {
    if (!settings.reactionsEnabled) {
      toast.error(t('reactionsDisabled'))
      return
    }
    if (visitorReactionTokens <= 0) {
      toast.error(t('reactionsRecharging'))
      return
    }
    actions.sendReaction(type)
    const coachPrompted = activeCoachMark?.id === 'reactions'
    demoSession?.trackMilestone('reaction_sent', { reactionType: type })
    demoSession?.trackMilestone('feature_used', { feature: 'reaction', coachPrompted })
  }

  const handleFeedback = (type: FeedbackType) => {
    actions.sendFeedback(type)
    setIsFeedbackSheetOpen(false)
    const coachPrompted = activeCoachMark?.id === 'feedback'
    demoSession?.trackMilestone('feedback_sent', { feedbackType: type })
    demoSession?.trackMilestone('feature_used', { feature: 'feedback', coachPrompted })
    toast.success(t('feedbackSent'))
  }

  const handleOpenBoostFromQueue = (request: DemoQueueRequest) => {
    setBoostingRequest(request)
    setIsBoostSheetOpen(true)
  }

  return (
    <ContainerContext.Provider value={container}>
      <div ref={containerRef} className="h-full flex flex-col bg-zinc-900 overflow-hidden relative">
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Branding Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800">
            <div className="flex items-center gap-1">
              <Music className="h-4 w-4 text-pink-500" />
              <span className="font-semibold text-white text-sm">iPerform</span>
              <span className="text-pink-500 text-sm">.live</span>
            </div>
            <button className="text-xs text-zinc-400 hover:text-white flex items-center gap-1">
              {t('areYouAnArtist')} <ArrowRight className="h-3 w-3" />
            </button>
          </div>

          {/* Cover Image / Gradient */}
          <div className="relative h-24 overflow-hidden">
            {artist.coverImageUrl ? (
              <Image
                src={artist.coverImageUrl}
                alt={`${artist.name} cover`}
                fill
                className="object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-purple-900 via-fuchsia-900 to-pink-900" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent" />
          </div>

          {/* Artist Header */}
          <div className="px-4 -mt-8 relative z-10">
            <div className="flex items-start gap-3">
              <Avatar className="h-14 w-14 ring-2 ring-red-500/50 border-2 border-zinc-900 shrink-0">
                <AvatarImage src={artist.profileImageUrl || ''} />
                <AvatarFallback className="bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white text-lg">
                  {artist.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 pt-2">
                <div className="flex items-center justify-between gap-2">
                  <h1 className="font-bold text-white text-lg truncate">{artist.name}</h1>
                  <div data-coach="social-links" className="flex items-center gap-1 shrink-0">
                    {artist.socialLinks.slice(0, 3).map(link => (
                      <button
                        key={link.id}
                        className="p-1.5 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors"
                      >
                        <SocialIcon platform={link.platform} />
                      </button>
                    ))}
                    {artist.socialLinks.length > 3 && (
                      <button className="p-1.5 rounded-full bg-zinc-800/80 hover:bg-zinc-700 text-zinc-400 hover:text-white transition-colors">
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    )}
                    <button className="p-1.5 rounded-full bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 transition-colors ml-1">
                      <Share2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className="bg-red-500/20 text-red-400 border-red-500/50 text-xs py-0">
                    <Radio className="h-3 w-3 mr-1 animate-pulse" />
                    {t('live')}
                  </Badge>
                  <span className="text-xs text-zinc-400 flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {t('watching', { count: visitorCount })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 pt-4 pb-4 space-y-4">
            {/* Break Banner */}
            {breakStatus.onBreak && (
              <Card className="border-amber-500/40 bg-gradient-to-r from-amber-950/50 to-orange-950/50">
                <CardContent className="py-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-amber-500/20 shrink-0">
                      <Coffee className="h-5 w-5 text-amber-400" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white">{t('breakBanner', { artistName: artist.name })}</p>
                      <p className="text-amber-200/80 text-xs">
                        {t('breakBackAt', {
                          time: breakStatus.expectedReturnAt
                            ? breakStatus.expectedReturnAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                            : t('breakBackSoon')
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Active Public Timers */}
            {publicTimers.length > 0 && (
              <div className="space-y-3">
                {publicTimers.map(timer => (
                  <TimerBanner key={timer.id} timer={timer} />
                ))}
              </div>
            )}

            {/* Now Playing + Previous */}
            {settings.showNowPlaying && (nowPlaying || lastPlayed) && (
              <NowPlayingSection
                nowPlaying={nowPlaying}
                lastPlayed={lastPlayed}
                onOpenHistory={() => setIsSongHistoryOpen(true)}
              />
            )}

            {/* Reactions */}
            {settings.reactionsEnabled && (
              <ReactionBar
                visitorReactionTokens={visitorReactionTokens}
                visitorHasTipped={visitorHasTipped}
                onReaction={handleReaction}
              />
            )}

            {/* Action Buttons - Request Song + Send Tip */}
            {(settings.requestsEnabled || settings.tipsEnabled) && (
              <div className={`grid gap-3 ${settings.requestsEnabled && settings.tipsEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {settings.requestsEnabled && (
                  <Button
                    data-coach="request-song"
                    className="h-14 text-base font-semibold bg-gradient-to-r from-violet-600 via-fuchsia-600 to-violet-600 hover:from-violet-500 hover:via-fuchsia-500 hover:to-violet-500 shadow-lg shadow-violet-500/25"
                    onClick={() => setIsRequestSheetOpen(true)}
                  >
                    <Music className="mr-2 h-5 w-5" />
                    {t('requestSong')}
                  </Button>
                )}
                {settings.tipsEnabled && (
                  <Button
                    data-coach="send-tip"
                    className="h-14 text-base font-semibold bg-gradient-to-r from-emerald-600 via-green-500 to-emerald-600 hover:from-emerald-500 hover:via-green-400 hover:to-emerald-500 text-white shadow-lg shadow-green-500/25"
                    onClick={() => setIsTipSheetOpen(true)}
                  >
                    <DollarSign className="mr-2 h-5 w-5" />
                    {t('sendTip')}
                  </Button>
                )}
              </div>
            )}

            {/* Song Queue */}
            {settings.showQueue && (
              <QueueSection
                queue={sortedQueue}
                settings={settings}
                visitorUpvotedRequestIds={visitorUpvotedRequestIds}
                onUpvote={handleUpvote}
                onBoost={handleOpenBoostFromQueue}
                onCancelBoost={actions.cancelBoost}
              />
            )}

            {/* Send Feedback */}
            {settings.feedbackEnabled && (
              <FeedbackSection
                artistName={artist.name}
                isFeedbackSheetOpen={isFeedbackSheetOpen}
                onOpenFeedbackSheet={() => setIsFeedbackSheetOpen(true)}
                onCloseFeedbackSheet={() => setIsFeedbackSheetOpen(false)}
                onSendFeedback={handleFeedback}
              />
            )}

            {/* Stay Connected Section */}
            <Card className="border-zinc-800 bg-zinc-900/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Heart className="h-4 w-4 text-pink-400" />
                  <span className="font-semibold text-white">{t('stayConnected', { artistName: artist.name })}</span>
                </div>
                <p className="text-sm text-zinc-400 mb-4">
                  {t('followForUpdates')}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { platform: 'spotify', label: 'Spotify', icon: SiSpotify },
                    { platform: 'instagram', label: 'Instagram', icon: SiInstagram },
                    { platform: 'youtube', label: 'YouTube', icon: SiYoutube },
                    { platform: 'facebook', label: 'Facebook', icon: SiFacebook },
                    { platform: 'tiktok', label: 'TikTok', icon: SiTiktok },
                    { platform: 'snapchat', label: 'Snapchat', icon: SiSnapchat },
                  ].map(({ platform, label, icon: Icon }) => (
                    <button
                      key={platform}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors text-sm"
                    >
                      <Icon className="h-4 w-4" />
                      <span>{label}</span>
                      <ExternalLink className="h-3 w-3 ml-auto text-zinc-500" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Shows Section */}
            <Card className="border-zinc-800 bg-zinc-900/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 text-amber-400" />
                  <span className="font-semibold text-white">{t('upcomingShows')}</span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/30 border border-zinc-800">
                    <div className="text-center shrink-0">
                      <div className="text-xs text-zinc-500 uppercase">Jan</div>
                      <div className="text-xl font-bold text-white">15</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white">{t('fridayNightLive')}</p>
                      <p className="text-sm text-zinc-400 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" />
                        {t('mockVenueBlueNoteCafe')}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">8:00 PM - 11:00 PM</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-zinc-800/30 border border-zinc-800">
                    <div className="text-center shrink-0">
                      <div className="text-xs text-zinc-500 uppercase">Jan</div>
                      <div className="text-xl font-bold text-white">22</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white">{t('acousticSessions')}</p>
                      <p className="text-sm text-zinc-400 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" />
                        {t('mockVenueRiversideLounge')}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">7:00 PM - 10:00 PM</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Footer */}
            <div className="text-center py-4 border-t border-zinc-800">
              <p className="text-xs text-zinc-600">
                {t('poweredBy')} <span className="text-pink-500">iPerform.live</span>
              </p>
            </div>
          </div>
        </div>

        {/* Sheets */}
        <RequestSheet
          open={isRequestSheetOpen}
          onClose={() => setIsRequestSheetOpen(false)}
          songs={songs}
          queue={sortedQueue}
          settings={settings}
          nowPlayingSongId={nowPlaying?.song.id ?? null}
          squelchedSongIds={squelchedSongIds}
          visitorUpvotedRequestIds={visitorUpvotedRequestIds}
          artistName={artist.name}
          onRequestSong={handleRequestSong}
          onUpvote={handleUpvote}
          onOpenBoost={(request) => {
            setIsRequestSheetOpen(false)
            setBoostingRequest(request)
            setIsBoostSheetOpen(true)
          }}
        />

        <TipSheet
          open={isTipSheetOpen}
          onClose={() => setIsTipSheetOpen(false)}
          settings={settings}
          artistName={artist.name}
          onSendTip={handleDirectTip}
        />

        <BoostSheet
          open={isBoostSheetOpen}
          onClose={() => {
            setIsBoostSheetOpen(false)
            setBoostingRequest(null)
          }}
          settings={settings}
          boostingRequest={boostingRequest}
          onBoost={handleBoost}
        />

        {/* Song History Sheet */}
        <InlineSheet
          open={isSongHistoryOpen}
          onClose={() => setIsSongHistoryOpen(false)}
          title={t('songHistory')}
          description={t('songsPlayedTonight', { count: playedSongs.length })}
        >
          <div className="p-4 space-y-2">
            {playedSongs.map((played, index) => (
              <div key={played.id} className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800/30">
                <span className="text-xs text-zinc-500 w-4">{playedSongs.length - index}</span>
                <SongArtwork
                  src={played.song.artworkUrl}
                  alt={played.song.title}
                  size={36}
                  className="h-9 w-9 rounded shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{played.song.title}</p>
                  <p className="text-xs text-zinc-400 truncate">{played.song.artist}</p>
                </div>
                {played.wasRequested && (
                  <Heart className="h-3 w-3 text-purple-400" fill="currentColor" />
                )}
              </div>
            ))}
            {playedSongs.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('noSongsPlayedYet')}</p>
              </div>
            )}
          </div>
        </InlineSheet>
      </div>
    </ContainerContext.Provider>
  )
}
